import { DayEntry, Task, TaskCheckOptions, Timestamp } from '@griply/types';
import {
  computeTimeslotRange,
  getScheduleByDate,
  getScheduleFrequency,
  getScheduleFrequencyDayTarget,
  getSlotLabelMode,
  SlotLabelKind,
} from '@griply/utils';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';
import { formatTimeRange } from 'shared/utils/format-time-range';
import { getTaskCheckOption } from 'shared/utils/get-task-check-option';
import { getTaskDayCompletionPercentage } from 'shared/utils/get-task-day-completion-percentage';

import { useData } from '../../use-data';

export type UseSlotDataOptions = {
  task: Task;
  date: Timestamp;
  entry?: DayEntry;
};

export const useSlotData = ({ task, date, entry }: UseSlotDataOptions) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn, timeFormat } = useData();

  const activeSchedule = useMemo(
    () => getScheduleByDate(task.schedules, { date, today }),
    [task.schedules, date, today],
  );

  const isToday = useMemo(() => isSameDay(date, today), [date, today]);

  const label = useMemo(() => {
    const frequencyUnit = activeSchedule
      ? getScheduleFrequency(activeSchedule)
      : undefined;
    const mode = getSlotLabelMode(date, { today, frequencyUnit });

    switch (mode.kind) {
      case SlotLabelKind.Today:
        return t('general.labels.dates.today');
      case SlotLabelKind.Tomorrow:
        return t('general.labels.dates.tomorrow');
      case SlotLabelKind.WeekdayWithDate:
        return format(mode.date, 'EEEE, d MMM');
      case SlotLabelKind.WeekdayShort:
        return format(mode.date, 'EEEE');
    }
  }, [date, t, today, activeSchedule]);

  const checked = useMemo(
    () =>
      getTaskCheckOption(task, {
        day: date,
        today,
        weekStartsOn,
        showRepeatingAsTodo: true,
      }),
    [date, task, today, weekStartsOn],
  );

  const completionPercentage = useMemo(
    () =>
      task?.schedules?.length
        ? getTaskDayCompletionPercentage(task, {
            day: date,
            weekStartsOn,
          })
        : undefined,
    [date, task, weekStartsOn],
  );

  const isFuture = useMemo(() => isAfter(date, today), [date, today]);

  const overdue = useMemo(
    () =>
      !!entry &&
      checked === TaskCheckOptions.Unchecked &&
      isBefore(date, today) &&
      !isSameDay(date, today),
    [checked, date, entry, today],
  );

  const target = useMemo(() => {
    if (!activeSchedule) {
      return;
    }

    return activeSchedule.frequency
      ? getScheduleFrequencyDayTarget(activeSchedule)
      : activeSchedule.completionTarget.count;
  }, [activeSchedule]);

  const timeLabel = useMemo(() => {
    const range = computeTimeslotRange(entry?.timeslot);
    if (!range) {
      return t('general.labels.all-day');
    }
    if (!range.endDate) {
      return format(range.startDate, timeFormat);
    }
    return formatTimeRange(range.startDate, range.endDate, timeFormat);
  }, [t, timeFormat, entry?.timeslot]);

  return {
    isToday,
    label,
    checked,
    completionPercentage,
    isFuture,
    overdue,
    target,
    timeLabel,
  };
};
